import { inspectionClient } from '../config/api';
import {
  ApiResponse,
  InspectionReason,
  CreateInspectionReasonRequest,
  UpdateInspectionReasonRequest,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class InspectionReasonService {
  private readonly basePath = '/api/inspectionreason';

  getAll = async (): Promise<InspectionReason[]> => {
    try {
      const response = await inspectionClient.get<ApiResponse<InspectionReason[]> | InspectionReason[]>(this.basePath);

      // Suporta tanto formato com wrapper quanto direto
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Erro ao buscar motivos de inspeção');
      }

      return response.data?.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao buscar motivos de inspeção');
    }
  };

  getById = async (id: string): Promise<InspectionReason> => {
    try {
      const response = await inspectionClient.get<ApiResponse<InspectionReason>>(
        `${this.basePath}/${id}`
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Erro ao buscar motivo de inspeção');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao buscar motivo de inspeção');
    }
  };

  create = async (data: CreateInspectionReasonRequest): Promise<string> => {
    try {
      const response = await inspectionClient.post<ApiResponse<string> | string>(this.basePath, data);

      // Suporta resposta direta ou com wrapper
      if (typeof response.data === 'string') {
        return response.data;
      }

      if (response.data?.success === false) {
        throw new Error(response.data?.errorMessage || 'Erro ao criar motivo de inspeção');
      }

      return response.data?.data || '';
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao criar motivo de inspeção');
    }
  };

  update = async (id: string, data: UpdateInspectionReasonRequest): Promise<void> => {
    try {
      const response = await inspectionClient.put<ApiResponse<void> | void>(
        `${this.basePath}/${id}`,
        data
      );

      // Só lança erro se explicitamente success = false
      if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success === false) {
        throw new Error((response.data as ApiResponse<void>)?.errorMessage || 'Erro ao atualizar motivo de inspeção');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao atualizar motivo de inspeção');
    }
  };

  delete = async (id: string): Promise<void> => {
    try {
      const response = await inspectionClient.delete<ApiResponse<void>>(
        `${this.basePath}/${id}`
      );

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Erro ao excluir motivo de inspeção');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao excluir motivo de inspeção');
    }
  };
}

export const inspectionReasonService = new InspectionReasonService();
